import {ListViewDataSourceDataItem, ListViewItemId} from './list-view-types';
import React from 'react';
import {HashingPrimitive, primitivesHashingFunction} from '@wix/devzai-utils-common';
import {reactIsValidReactElement} from "../react-utils/react-utils";

/**
 * @deprecated use listViewCreateDataSource instead
 */
export function createDataSource<T>(
    array: Array<T>,
    {
        isSelectable = true,
        isDisabled = false,
        idFunction = dataItem => `${dataItem}`,
        typeAheadTextFunction = dataItem => `${dataItem}`
    }: {
        idFunction?: (dataItem: T, index: number) => ListViewItemId;
        typeAheadTextFunction?: (dataItem: T) => string | undefined;
        isSelectable?: boolean | ((dataItem: T, index: number) => boolean);
        isDisabled?: boolean | ((dataItem: T, index: number) => boolean);
    } = {}
): ListViewDataSourceDataItem<T>[] {
    return array.map((item, index) => {
        const isItemSelectable = typeof isSelectable === 'boolean' ? isSelectable : isSelectable(item, index);
        const isItemDisabled = typeof isDisabled === 'boolean' ? isDisabled : isDisabled(item, index);

        return {
            id: idFunction(item, index),
            dataItem: item,
            typeAheadText: typeAheadTextFunction ? typeAheadTextFunction(item) : undefined,
            isSelectable: isItemSelectable,
            isDisabled: isItemDisabled
        };
    });
}

export interface IListViewCreateDataSourceOptions<T> {
    idFunction?: (dataItem: T, index: number) => ListViewItemId;
    typeAheadTextFunction?: (dataItem: T) => string | undefined;
    isSelectable?: boolean | ((dataItem: T, index: number) => boolean);
    isDisabled?: boolean | ((dataItem: T, index: number) => boolean);
}

/**
 * Creates a data source that's suitable for ListView and ListViewComposable.
 * @param array An array of data items
 * @param {Object} options
 * @param options.idFunction
 * A function called per data item to extract its id.
 * @param options.isSelectable
 * One of the following:
 * - A true|false value that indicates whether data items are selectable or not
 * - A predicate, which invoked per data item to determine of whether a data item is selectable or not.
 * @param options.isDisabled
 * One of the following:
 * - A true|false value that indicates whether data items are disabled or not
 * - A predicate, which invoked per data item to determine of whether a data item is disabled or not.
 * @param options.typeAheadTextFunction
 * A function that called per data item to extract its type ahead text.
 */
export function listViewCreateDataSource<T>(
    array: (T)[],
    options?: IListViewCreateDataSourceOptions<T>
): ListViewDataSourceDataItem<T>[]
export function listViewCreateDataSource<T>(
    array: (T | React.ReactElement)[],
    options?: IListViewCreateDataSourceOptions<T>
): (ListViewDataSourceDataItem<T> | React.ReactElement)[]
export function listViewCreateDataSource<T>(
    array: (T | React.ReactElement)[],
    options: IListViewCreateDataSourceOptions<T> = {}
): (ListViewDataSourceDataItem<T> | React.ReactElement)[] {

    const {
        isSelectable = true,
        isDisabled = false,
        idFunction = (_, index) => index,
        typeAheadTextFunction = defaultTypeAheadFunction
    } = options;

    return array.map((item, index) => {
        if (reactIsValidReactElement(item)) {
            return item;
        }

        const isItemSelectable = typeof isSelectable === 'boolean' ? isSelectable : isSelectable(item, index);
        const isItemDisabled = typeof isDisabled === 'boolean' ? isDisabled : isDisabled(item, index);

        return {
            id: idFunction(item, index),
            dataItem: item,
            typeAheadText: typeAheadTextFunction ? typeAheadTextFunction(item) : undefined,
            isSelectable: isItemSelectable,
            isDisabled: isItemDisabled
        };
    });
}

export function listViewCreateDataSourceForGroups<T>(
    array: (T | T[])[],
    options: IListViewCreateDataSourceOptions<T> = {}
): (ListViewDataSourceDataItem<T> | ListViewDataSourceDataItem<T>[])[] {

    const {
        isSelectable = true,
        isDisabled = false,
        idFunction = (_, index) => index,
        typeAheadTextFunction = defaultTypeAheadFunction
    } = options;

    return array.map((item, index) => {
        if (Array.isArray(item)) {
            return listViewCreateDataSource(item, options);
        }

        const isItemSelectable = typeof isSelectable === 'boolean' ? isSelectable : isSelectable(item, index);
        const isItemDisabled = typeof isDisabled === 'boolean' ? isDisabled : isDisabled(item, index);

        return {
            id: idFunction(item, index),
            dataItem: item,
            typeAheadText: typeAheadTextFunction ? typeAheadTextFunction(item) : undefined,
            isSelectable: isItemSelectable,
            isDisabled: isItemDisabled
        };
    });
}

function defaultTypeAheadFunction<T> (value: T) {
    switch (typeof value) {
        case 'number':
        case 'string':
        case 'boolean':
            return value.toString();
        default:
            return undefined;
    }
}

export function listViewCreatePrimitivesDataSource<T extends HashingPrimitive>(
    array: (T)[],
    options?: IListViewCreateDataSourceOptions<T>
): ListViewDataSourceDataItem<T>[]
export function listViewCreatePrimitivesDataSource<T extends HashingPrimitive>(
    array: (T | React.ReactElement)[],
    options?: IListViewCreateDataSourceOptions<T>
): (ListViewDataSourceDataItem<T> | React.ReactElement)[]
export function listViewCreatePrimitivesDataSource<T extends HashingPrimitive>(
    array: (T | React.ReactElement)[],
    options: IListViewCreateDataSourceOptions<T> = {}
): (ListViewDataSourceDataItem<T> | React.ReactElement)[] {

    const {
        isSelectable = true,
        isDisabled = false,
        idFunction = primitivesHashingFunction,
        typeAheadTextFunction = listViewPrimitivesTypeAheadTextFunction
    } = options;

    return listViewCreateDataSource(array, {
        isSelectable,
        isDisabled,
        idFunction,
        typeAheadTextFunction
    });
}

function listViewPrimitivesTypeAheadTextFunction<T extends HashingPrimitive> (value: T) {
    switch (typeof value) {
        case 'string':
        case 'number':
        case 'boolean':
            return value.toString();
        default:
            return undefined;
    }
}
